import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ModalOverlay } from '@/components/ui/modalOverlay';
import { Textarea } from '@/components/ui/textarea';
import { isBlank } from '@/utils/formValidation';

interface RejectPetChangeRequestDialogProps {
  petName: string;
  isSubmitting: boolean;
  onConfirm: (reviewNote: string) => void;
  onCancel: () => void;
}

export function RejectPetChangeRequestDialog({
  petName,
  isSubmitting,
  onConfirm,
  onCancel,
}: RejectPetChangeRequestDialogProps) {
  const { t } = useTranslation(['patients', 'common']);
  const [reviewNote, setReviewNote] = useState('');
  const [showReasonError, setShowReasonError] = useState(false);

  function confirm() {
    if (isBlank(reviewNote)) {
      setShowReasonError(true);
      return;
    }
    onConfirm(reviewNote.trim());
  }

  return (
    <ModalOverlay onRequestClose={onCancel} className="z-[60]">
      <div className="w-full max-w-md bg-zoowhite border border-zooborder rounded-3xl shadow-2xl p-6">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4 bg-zooerrorbg">
          <Icon name="X" className="text-xl text-zooerrortext" />
        </div>
        <h2 className="text-lg font-bold text-zooindigo">
          {t('patients:changeRequest.rejectDialog.title', { petName })}
        </h2>
        <p className="text-sm text-zoomuted mt-1.5">
          {t('patients:changeRequest.rejectDialog.hint')}
        </p>

        <div className="space-y-1.5 mt-4">
          <Label htmlFor="pet-change-request-reject-note">
            {t('patients:changeRequest.rejectDialog.reasonLabel')}
          </Label>
          <Textarea
            id="pet-change-request-reject-note"
            rows={3}
            value={reviewNote}
            placeholder={t('patients:changeRequest.rejectDialog.reasonPlaceholder')}
            onChange={function update(changeEvent) {
              setReviewNote(changeEvent.target.value);
              setShowReasonError(false);
            }}
          />
          {showReasonError === true ? (
            <p className="text-xs font-medium text-zooerrortext">
              {t('patients:changeRequest.rejectDialog.reasonRequired')}
            </p>
          ) : null}
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            className="flex-1"
            isLoading={isSubmitting}
            onClick={confirm}
          >
            {t('patients:changeRequest.rejectDialog.confirm')}
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
